import { uuid } from "@xapp/chat-widget-core";
import { ChatUserInfo } from "@xapp/stentor-chat-widget";
import io from "socket.io-client";
import { setAccountStatus, setConnectionStatus } from "../store/actions";
import receiveToken from "../store/actions/receiveToken";
import { ActionType } from "../store/ChatAction";
import { log } from "../utils";
import { ChatServer, ChatServerOptions, OfflineMessage } from "./ChatServer";
import { ChatMessageRequest, ChatServerMessage } from "./ChatServerMessage";

export interface StentorServerChatConfig {
    readonly url: string;
}

export class StentorServerChat implements ChatServer {
    private socket!: SocketIOClient.Socket;
    private dispatch!: (action: ActionType) => void;

    private visitorInfo!: ChatUserInfo;
    private token: string;

    private readonly config: StentorServerChatConfig;
    // private readonly options: ChatServerOptions | undefined;

    constructor(config: StentorServerChatConfig, options?: ChatServerOptions) {
        this.config = config;
        // this.options = options;
        this.token = options.token;
    }

    public init(dispatch: (action: ActionType) => void): void {
        this.socket = io(this.config.url, {
            transports: ["websocket"],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: 5
        });

        this.dispatch = dispatch;

        // TODO: Authenticate - or at least tell the server who you are

        // Socket events

        this.socket.on("account_status", (data: Record<string, unknown>) => {
            log(`SERVER: account_status: ${JSON.stringify(data)}`);

            // Register the "communication" with the server
            if (!this.token) {
                this.token = uuid();
                dispatch(receiveToken(this.token));
                log(`CLIENT: created token: ${this.token}`);
            }

            this.socket.emit("connection update", this.token);

            dispatch({
                type: "account_status",
                detail: {
                    status: "online",
                    timestamp: +new Date()
                }
            });
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.socket.on("new message", (data: any): void => {
            log(`SERVER: new message: ${JSON.stringify(data)}`);

            this.dispatch({
                type: "chat",
                detail: {
                    type: "chat.msg",
                    user: {
                        displayName: data.username,
                        nick: "agent:robot",
                    },
                    msg: data.message,
                    timestamp: +new Date()
                }
            });
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.socket.on("user joined", (data: any): void => {
            log(`SERVER: user joined: ${JSON.stringify(data)}`);

            this.visitorInfo.token = data.token;

            this.dispatch({
                type: "chat",
                detail: {
                    type: "chat.memberjoin",
                    user: {
                        displayName: data.username,
                        nick: "agent:robot",
                        avatarPath: data.avatarPath
                    },
                    timestamp: +new Date(),
                }
            });
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.socket.on("user left", (data: any) => {
            log(`SERVER: user left: ${JSON.stringify(data)}`);

            this.dispatch({
                type: "chat",
                detail: {
                    type: "chat.memberleave",
                    user: {
                        displayName: data.username,
                        nick: "agent:robot",
                    },
                    timestamp: +new Date()
                }
            });
        });

        this.socket.on("typing", (data: Record<string, unknown>) => {
            log(`SERVER: typing: ${JSON.stringify(data)}`);

            this.dispatch({
                type: "chat",
                detail: {
                    type: "chat.typing",
                    user: {
                        nick: "agent:robot",
                    },
                    typing: true,
                    timestamp: +new Date()
                }
            });
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.socket.on("stop typing", (data: any) => {
            log(`SERVER: stop typing: ${JSON.stringify(data)}`);

            this.dispatch({
                type: "chat",
                detail: {
                    type: "chat.typing",
                    user: {
                        nick: "agent:robot",
                    },
                    typing: false,
                    timestamp: +new Date()
                }
            });
        });

        this.socket.on("disconnect", () => {
            log("SERVER: disconnect");
            this.dispatch(setAccountStatus("offline"));
            this.dispatch(setConnectionStatus("offline"));
        });

        this.socket.on("reconnect", () => {
            log("SERVER: reconnect");
            this.dispatch(setAccountStatus("online"));
            this.dispatch(setConnectionStatus("online"));
        });

        this.socket.on("reconnect_failed", (data: Record<string, unknown>) => {
            log("SERVER: reconnect_failed", data);
            this.dispatch(setAccountStatus("offline"));
            this.dispatch(setConnectionStatus("offline"));
        });

        this.socket.on("reconnect_error", (data: Record<string, unknown>) => {
            log("SERVER: reconnect_error", data);
            this.dispatch(setAccountStatus("offline"));
        });

        this.dispatch(setConnectionStatus("pending"));

    };

    public sendOfflineMsg(_: OfflineMessage, cb: (error?: Error) => void): void {
        cb();
    }

    public sendChatMsg(message: ChatServerMessage, cb: (err?: Error) => void): void {
        this.socket.emit("new message", message);
        cb();
    }

    public sendChatMsgRequest(message: ChatMessageRequest, cb: (err?: Error) => void): void {
        this.socket.emit("new message", message);
        cb();
    }

    public sendTyping(isTyping: boolean): void {
        this.socket.emit(isTyping ? "typing" : "stop typing");
    }

    public setVisitorInfo(visitorInfo: ChatUserInfo, _sessionId: string, cb: (error?: Error) => void): void {
        this.visitorInfo = visitorInfo;
        this.socket.emit("add user", visitorInfo);
        cb();
    }

    public sendChatRating(): void {
    }

    public sendFile(_: File, cb: (err?: Error) => void): void {
        cb();
    }

    public markAsRead(): void { // mark the messages read
    }

    public flush(): void {
    }

    public dispose(): void {
        this.socket?.disconnect();
    }

    public sleep(): void {
    }

    public wakeup(): void {
    }
    
    public async bargeOut(_cb: (err?: Error) => void): Promise<void> {
        throw new Error("Method not implemented: bargeOut");
    }

    public async bargeIn(_agentName: string, _cb: (err?: Error) => void): Promise<void> {
        throw new Error("Method not implemented: bargeIn");
    }
}
